/**
 * The job card's assistant, as the API and the web both see it.
 *
 * A tool is one of three tiers, and the tier is the whole safety story:
 *
 * - `read` runs as soon as the model asks, and changes nothing.
 * - `immediate` writes straight away — a note, a follow-up, a person — and
 *   leaves a receipt with Undo. Nothing in this tier reaches a customer,
 *   books a visit or touches money.
 * - `approve` waits on a person's click: anything that goes to a customer,
 *   books or moves a visit, changes the stage, or prices, issues or sends a
 *   document. The database refuses an `EXECUTED` row with `requiresApproval`
 *   and no approver (decision 9).
 *
 * Every tool runs an ordinary procedure as the person asking, so the
 * permission checks, the locks and the send rules are the buttons' own.
 * Decision 94.
 */

export const ASSISTANT_TIERS = ['read', 'immediate', 'approve'] as const
export type AssistantTier = (typeof ASSISTANT_TIERS)[number]

export type AssistantToolStatus = 'PROPOSED' | 'AWAITING_APPROVAL' | 'EXECUTED' | 'REJECTED' | 'FAILED'

export type AssistantCardKind =
  | 'email'
  | 'document'
  | 'booking'
  | 'lines'
  | 'stage'
  | 'proposal'
  | 'invoice'
  | 'sms'
  | 'note'
  | 'task'
  | 'contact'
  | 'service'
  | 'brief'

/** One line of a price, as a card draws it. Integer cents inc GST. */
export interface AssistantCardLine {
  description: string
  quantity: string
  unitPriceIncTax: number
  totalIncTax: number
}

/**
 * What a card shows. Drawn from the tool's arguments when it is proposed, so
 * the person approves exactly the thing that will run.
 */
export interface AssistantCard {
  kind: AssistantCardKind
  title: string
  subtitle?: string | null
  body?: string | null
  fields?: Array<{ label: string; value: string }>
  lines?: AssistantCardLine[]
  totalIncTax?: number | null
  attachments?: Array<{ id: string; name: string }>
  /** The records the words drew on — the mockup's "Used" chips. */
  sources?: string[]
  /** Said above the buttons, when approving would be refused or surprising. */
  warning?: string | null
  /** For Edit in composer: the email exactly as it would go. */
  email?: {
    to: string[]
    cc: string[]
    bcc: string[]
    subject: string
    body: string
    attachmentIds: string[]
  } | null
  /** For an SMS draft: the phone's own compose window takes it from here. */
  sms?: { to: string | null; body: string } | null
}

export interface AssistantToolCallView {
  id: string
  /** The assistant turn that asked for it. */
  turnId: string | null
  toolName: string
  tier: AssistantTier
  status: AssistantToolStatus
  /** A step's words while it runs, and a receipt's afterwards. */
  label: string
  card: AssistantCard | null
  error: string | null
  rejectedReason: string | null
  approvedByName: string | null
  /** Whether Undo is still offered. */
  undoable: boolean
  undone: boolean
}

export interface AssistantTurnView {
  id: string
  role: 'USER' | 'ASSISTANT'
  content: string
  occurredAt: Date
  toolCalls: AssistantToolCallView[]
}

export interface AssistantSessionView {
  id: string
  jobId: string
  startedAt: Date
  turns: AssistantTurnView[]
}

/** What `POST /assistant/run` accepts. */
export type AssistantRunRequest =
  | { sessionId: string; action: 'summary' }
  | { sessionId: string; action: 'message'; text: string }
  | { sessionId: string; action: 'approve'; toolCallId: string }
  | { sessionId: string; action: 'reject'; toolCallId: string; reason?: string; resume?: boolean }

/**
 * One line of the NDJSON stream. `text` is the reply arriving; `toolCall` is
 * a step or a card, sent again whenever its status moves; `done` carries the
 * turns as saved, which replace whatever was drawn from the stream.
 */
export type AssistantStreamEvent =
  | { type: 'turn'; turn: AssistantTurnView }
  | { type: 'text'; delta: string }
  | { type: 'toolCall'; toolCall: AssistantToolCallView }
  | { type: 'done'; session: AssistantSessionView }
  | { type: 'error'; message: string }

/** The chips under the conversation. Plain prompts, sent as if typed. */
export const ASSISTANT_SUGGESTIONS = [
  { label: 'Draft technician brief', prompt: 'Draft a brief for the technician going to this job: what to do, access, hazards and anything the customer asked for.' },
  { label: 'Booking confirmation SMS', prompt: 'Draft an SMS to the job contact confirming the next booking.' },
  { label: 'Suggest a time', prompt: 'Suggest a few times in the next week to book this job, and who could go.' },
  { label: 'Create follow-up task', prompt: 'Create a follow-up task for the most useful next step on this job.' },
  { label: 'Summarise site history', prompt: 'Summarise the history of work at this property.' },
] as const
